// src/Dashboard/pages/user/DashboardUser/components/ObjectifDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import { FaSave } from "react-icons/fa";
import { toast } from "react-toastify";

import DashboardDrawer from "./DashboardDrawer";
import { getCurrentSeasonLabel } from "../utils/dashboardUserUtils";

const TYPES = [
  "loisir",
  "ceinture",
  "poids",
  "renforcement",
  "competition",
  "kata",
  "uv2",
  "uv3_points",
  "autre",
];

export default function ObjectifDrawer({
  open,
  onClose,
  onSubmit,
  initial,
  adherentId,
}) {
  const isEdit = !!initial?.id;

  const [title, setTitle] = useState("");
  const [type, setType] = useState("");
  const [saison, setSaison] = useState(getCurrentSeasonLabel());
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [progress, setProgress] = useState(0);
  const [isDone, setIsDone] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    setTitle(initial?.title || "");
    setType(initial?.type || "");
    setSaison(initial?.saison || getCurrentSeasonLabel());
    setDescription(initial?.description || "");
    setStartDate(initial?.start_date?.slice?.(0, 10) || "");
    setDueDate(initial?.due_date?.slice?.(0, 10) || "");
    setProgress(typeof initial?.progress === "number" ? initial.progress : 0);
    setIsDone(!!initial?.is_done);
    setSaving(false);
  }, [open, initial]);

  const canSubmit = useMemo(() => {
    return !!adherentId && title.trim().length > 0 && !saving;
  }, [adherentId, title, saving]);

  const handleProgress = (value) => {
    const next = Math.max(0, Math.min(100, Number(value) || 0));
    setProgress(next);

    if (next >= 100) {
      setIsDone(true);
    }
  };

  const handleDone = (checked) => {
    setIsDone(checked);

    if (checked && progress < 100) {
      setProgress(100);
    }

    if (!checked && progress >= 100) {
      setProgress(99);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.error("Le titre est requis.");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        adherent_id: adherentId,
        title: title.trim(),
        type: type || null,
        saison: saison || null,
        description: description || null,
        start_date: startDate || null,
        due_date: dueDate || null,
        progress: Number(progress) || 0,
        is_done: !!isDone,
      };

      await onSubmit(payload, initial?.id || null);
      onClose?.();
    } catch (error) {
      console.error("[ObjectifDrawer] submit error:", error);
      toast.error("Échec de l’enregistrement de l’objectif.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardDrawer
      open={open}
      onClose={onClose}
      title={isEdit ? "Modifier l’objectif" : "Nouvel objectif"}
      subtitle="Définissez un objectif de saison pour l’adhérent sélectionné."
      size="lg"
      footer={
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          >
            Annuler
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 px-4 py-2 text-sm font-bold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      }
    >
      <div className="space-y-5">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
          <h3 className="font-bold text-slate-900 dark:text-white">
            Informations
          </h3>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                Titre <span className="text-rose-600">*</span>
              </label>
              <input
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Ex. Obtenir la ceinture verte"
                className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-2 focus:ring-violet-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                Type
              </label>
              <select
                value={type}
                onChange={(event) => setType(event.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-2 focus:ring-violet-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              >
                <option value="">—</option>
                {TYPES.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                Saison
              </label>
              <input
                value={saison}
                onChange={(event) => setSaison(event.target.value)}
                placeholder="2025-2026"
                className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-2 focus:ring-violet-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                Description
              </label>
              <textarea
                rows={4}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Détails, étapes, remarques…"
                className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-2 focus:ring-violet-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              />
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
          <h3 className="font-bold text-slate-900 dark:text-white">
            Suivi et échéance
          </h3>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                Date de début
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-2 focus:ring-violet-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                Échéance
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={(event) => setDueDate(event.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-2 focus:ring-violet-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                Progression : {progress}%
              </label>
              <input
                type="range"
                min={0}
                max={100}
                value={progress}
                onChange={(event) => handleProgress(event.target.value)}
                className="mt-2 w-full"
              />
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-emerald-500 via-violet-500 to-orange-400"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            <label className="sm:col-span-2 flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 dark:border-slate-800 dark:bg-slate-950">
              <input
                type="checkbox"
                checked={isDone}
                onChange={(event) => handleDone(event.target.checked)}
                className="h-4 w-4"
              />
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                Objectif terminé
              </span>
            </label>
          </div>
        </div>
      </div>
    </DashboardDrawer>
  );
}